import React from 'react';
import { Handshake } from '@phosphor-icons/react';

// Analytics API row shape from learn_my_coaching metric
interface CoachingRow {
    id: string | number;
    status: string;
    progress: number;
    completedSessions: number;
    totalSessions: number;
    startDate?: string;
    nextSessionDate?: string;
    programName?: string;
    coachName?: string;
}

// Analytics API row shape from learn_my_coaching_goals metric
interface CoachingGoalRow {
    id: string | number;
    title: string;
    progress: number;
}

interface MyCoachingJourneyWidgetProps {
    data?: CoachingRow[] | null;
    goalsData?: CoachingGoalRow[] | null;
    loading?: boolean;
    goalsLoading?: boolean;
    error?: string;
}

const getInitials = (name: string): string => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
        case 'active': return { label: 'Active', className: 'bg-emerald-200 text-emerald-800' };
        case 'completed': return { label: 'Completed', className: 'bg-blue-200 text-blue-800' };
        case 'paused': return { label: 'Paused', className: 'bg-gray-200 text-gray-700' };
        default: return { label: status || 'Pending', className: 'bg-gray-200 text-gray-700' };
    }
};

const getGoalColor = (progress: number) => {
    if (progress >= 60) return { dot: 'bg-emerald-500', bar: 'bg-emerald-500', text: 'text-emerald-600' };
    if (progress >= 30) return { dot: 'bg-amber-500', bar: 'bg-amber-500', text: 'text-amber-600' };
    return { dot: 'bg-red-500', bar: 'bg-red-500', text: 'text-red-600' };
};

const formatShortDate = (dateStr?: string) => {
    if (!dateStr) return '\u2014';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const MyCoachingJourneyWidget: React.FC<MyCoachingJourneyWidgetProps> = ({ data, goalsData, loading, goalsLoading, error }) => {
    const rows = Array.isArray(data) ? data : [];
    const coaching = rows.length > 0 ? rows[0] : null;
    const goals = Array.isArray(goalsData) ? goalsData : [];
    const isLoading = loading || goalsLoading;

    return (
        <div className="flex flex-col h-full">
<div className="flex-1 overflow-auto px-4 pb-4">
                {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : !coaching ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <Handshake className="w-8 h-8" />
                        <span className="text-sm">No coaching engagement</span>
                    </div>
                ) : (
                    <>
                        <div className="p-4 bg-emerald-50/50 border border-emerald-200 rounded-lg mb-3">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-emerald-200 flex items-center justify-center text-emerald-700 text-sm font-bold">
                                    {coaching.coachName ? getInitials(coaching.coachName) : '??'}
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-900">Coach: {coaching.coachName || 'Unknown'}</div>
                                    <div className="text-[11px] text-gray-500">
                                        {coaching.programName || 'Coaching'}
                                        {coaching.startDate && ` · Since ${formatShortDate(coaching.startDate)}`}
                                    </div>
                                </div>
                                <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full font-medium ${getStatusBadge(coaching.status).className}`}>
                                    {getStatusBadge(coaching.status).label}
                                </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <div className="text-center p-2 bg-white rounded-lg">
                                    <div className="text-lg font-bold text-emerald-600">{Number(coaching.completedSessions) || 0}</div>
                                    <div className="text-[10px] text-gray-400">Sessions Done</div>
                                </div>
                                <div className="text-center p-2 bg-white rounded-lg">
                                    <div className="text-lg font-bold text-primary">{goals.length}</div>
                                    <div className="text-[10px] text-gray-400">Goals Active</div>
                                </div>
                                <div className="text-center p-2 bg-white rounded-lg">
                                    <div className="text-lg font-bold text-amber-600">{formatShortDate(coaching.nextSessionDate)}</div>
                                    <div className="text-[10px] text-gray-400">Next Session</div>
                                </div>
                            </div>
                        </div>
                        {goals.length > 0 && (
                            <>
                                <div className="text-xs text-gray-500 font-medium mb-2">Active Goals</div>
                                <div className="space-y-2">
                                    {goals.map((goal) => {
                                        const progress = Number(goal.progress) || 0;
                                        const colors = getGoalColor(progress);
                                        return (
                                            <div key={goal.id} className="flex items-center gap-3">
                                                <div className={`w-1.5 h-1.5 rounded-full ${colors.dot} shrink-0`} />
                                                <div className="flex-1">
                                                    <span className="text-sm text-gray-700">{goal.title}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                        <div className={`h-full ${colors.bar} rounded-full`} style={{ width: `${progress}%` }} />
                                                    </div>
                                                    <span className={`text-[10px] font-medium ${colors.text}`}>{progress}%</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
